/**
 * useMLMetricDrawer Hook
 * ============================================================================
 * Custom React hook for managing the ML Readiness Metric Drawer state
 * and fetching metric details from the API.
 *
 * Usage:
 * ```tsx
 * const { isOpen, metricDetail, loading, error, openDrawer, closeDrawer } =
 *   useMLMetricDrawer(jobId);
 *
 * // Later, listen to events:
 * useEffect(() => {
 *   const handler = (e) => openDrawer(e.detail.metricId);
 *   window.addEventListener('ml-metric-click', handler);
 *   return () => window.removeEventListener('ml-metric-click', handler);
 * }, [openDrawer]);
 * ```
 *
 * Related Files:
 * - client/types/mlReadinessMetric.ts (type definitions)
 * - client/components/mlReadiness/MLReadinessMetricDrawer.tsx (UI component)
 *
 * @version 1.0.0
 * @created 2026-01-29
 * ============================================================================
 */

import { useState, useCallback } from 'react';
import type {
  MLReadinessMetricId,
  MLReadinessMetricDetail,
  GetMLMetricDetailResponse,
} from '../../types/mlReadinessMetric';
import { isValidMetricId } from '../../types/mlReadinessMetric';

// ============================================================================
// Types
// ============================================================================

export interface UseMLMetricDrawerReturn {
  /** Whether the drawer is currently open */
  isOpen: boolean;
  /** The metric detail data (null if not loaded) */
  metricDetail: MLReadinessMetricDetail | null;
  /** Loading state */
  loading: boolean;
  /** Error message (null if no error) */
  error: string | null;
  /** The currently selected metric ID */
  selectedMetricId: MLReadinessMetricId | null;
  /** Open the drawer and fetch details for a metric */
  openDrawer: (metricId: MLReadinessMetricId) => Promise<void>;
  /** Close the drawer */
  closeDrawer: () => void;
  /** Reset error state */
  clearError: () => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for managing ML Readiness Metric Drawer state and API calls
 *
 * @param jobId - The ML Readiness job ID to fetch metric details from
 * @returns Drawer state and control functions
 */
export function useMLMetricDrawer(jobId: string | null): UseMLMetricDrawerReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [metricDetail, setMetricDetail] = useState<MLReadinessMetricDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetricId, setSelectedMetricId] = useState<MLReadinessMetricId | null>(null);

  /**
   * Fetch metric details from the API
   */
  const fetchMetricDetail = useCallback(
    async (metricId: MLReadinessMetricId): Promise<MLReadinessMetricDetail | null> => {
      if (!jobId) {
        throw new Error('No job ID provided');
      }

      const response = await fetch(`/api/ml-readiness/${jobId}/metrics/${metricId}`);
      const data: GetMLMetricDetailResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to fetch metric details: ${response.statusText}`);
      }

      return data.data || null;
    },
    [jobId]
  );

  /**
   * Open the drawer and fetch details for a metric
   */
  const openDrawer = useCallback(
    async (metricId: MLReadinessMetricId) => {
      // Validate metric ID
      if (!isValidMetricId(metricId)) {
        setError(`Invalid metric ID: ${metricId}`);
        return;
      }

      // Check for job ID
      if (!jobId) {
        setError('No ML Readiness job ID available');
        return;
      }

      // Set state for loading
      setSelectedMetricId(metricId);
      setIsOpen(true);
      setLoading(true);
      setError(null);
      setMetricDetail(null);

      try {
        const detail = await fetchMetricDetail(metricId);
        setMetricDetail(detail);
      } catch (err: unknown) {
        console.error(`[useMLMetricDrawer] Failed to fetch ${metricId}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load metric details');
      } finally {
        setLoading(false);
      }
    },
    [jobId, fetchMetricDetail]
  );

  /**
   * Close the drawer
   */
  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    // Don't clear metric detail immediately to allow for close animation
    setTimeout(() => {
      setMetricDetail(null);
      setSelectedMetricId(null);
      setError(null);
    }, 300);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isOpen,
    metricDetail,
    loading,
    error,
    selectedMetricId,
    openDrawer,
    closeDrawer,
    clearError,
  };
}

export default useMLMetricDrawer;
